/**
 * ALBPE-20 — Cookie consent gate + persistent location storage.
 *
 * Owns the STORAGE layer only (PRD Section 16 "Location Persistence", FR-09/FR-10,
 * NFR-03). It does NOT draw the consent banner (that is Shopify's native Customer
 * Privacy banner, configured in admin, read via window.Shopify.customerPrivacy) and
 * it does NOT resolve location markets (that is catalog-bootstrap.js).
 *
 * Responsibilities:
 *   - Read consent (preferences category) and default to DENIED until proven granted.
 *   - Persist { postalCode, retailLocation, catalogId } respecting that consent:
 *       consent granted -> localStorage (survives sessions)
 *       consent denied  -> sessionStorage only (dies with the tab)
 *   - Restore + validate on load; re-prompt (return null) if the saved catalog is invalid.
 *   - Clear on demand (nav switcher / FR-11).
 *   - React when consent changes mid-session (upgrade session->persistent, or purge).
 *
 * Public surface (all under window.Maximize.locationPersistence):
 *   save({ postalCode, retailLocation, catalogId }) -> normalized state | null
 *   restore() -> state | null            get() -> state | null
 *   clear() -> void                      hasConsent() -> boolean
 *
 * Events dispatched on `document` (detail shapes in docs/ALBPE-20-location-persistence.md):
 *   maximize:location:saved            maximize:location:restored
 *   maximize:location:cleared          maximize:location:consent-changed
 */
if (!window.Maximize?.loadedScript?.includes("location-persistence.js")) {
  if (window.Maximize?.loadedScript) {
    window.Maximize.loadedScript.push("location-persistence.js");
  }

  (function () {
    const STORAGE_KEY = "a2z_location";
    const SCHEMA_VERSION = 1;

    const EVENT = {
      saved: "maximize:location:saved",
      restored: "maximize:location:restored",
      cleared: "maximize:location:cleared",
      consentChanged: "maximize:location:consent-changed",
    };

    // ---- consent adapter (the ALBPE-26 / D-09 seam) -------------------------
    // The ONLY place that knows how consent is read. Uses Shopify's native
    // Customer Privacy API. A remembered location is a functional/"preferences"
    // cookie, so we gate on preferencesProcessingAllowed(), not analytics.
    function readConsentSync() {
      try {
        const cp = window.Shopify && window.Shopify.customerPrivacy;
        if (cp && typeof cp.preferencesProcessingAllowed === "function") {
          return cp.preferencesProcessingAllowed() === true;
        }
      } catch (_) {
        /* API not ready */
      }
      return false; // default-deny (privacy-safe, NFR-03)
    }

    const store = {
      _consent: false, // default-deny until the API resolves
      _state: null,

      hasConsent() {
        return this._consent === true;
      },

      // ---- storage core -----------------------------------------------------
      save(input) {
        const state = this._normalize(input);
        if (!state) return null;
        this._state = state;
        this._write(state);
        document.dispatchEvent(
          new CustomEvent(EVENT.saved, {
            detail: { location: state, persisted: this.hasConsent() },
          })
        );
        return state;
      },

      restore() {
        const state = this._read();
        if (!state) return null;
        if (!this._validate(state)) {
          // AC: "validate catalog still exists before restoring — re-prompt if invalid".
          this.clear();
          return null;
        }
        this._state = state;
        document.dispatchEvent(
          new CustomEvent(EVENT.restored, { detail: { location: state } })
        );
        return state;
      },

      get() {
        return this._state || this._read();
      },

      clear() {
        this._state = null;
        this._remove(localStorage);
        this._remove(sessionStorage);
        document.dispatchEvent(new CustomEvent(EVENT.cleared));
      },

      // ---- internals --------------------------------------------------------
      _write(state) {
        const raw = JSON.stringify(state);
        try {
          if (this.hasConsent()) {
            localStorage.setItem(STORAGE_KEY, raw);
            sessionStorage.removeItem(STORAGE_KEY);
          } else {
            // Never persist beyond the session without consent (FR-10 / AC-07).
            sessionStorage.setItem(STORAGE_KEY, raw);
            localStorage.removeItem(STORAGE_KEY);
          }
        } catch (_) {
          /* storage may be unavailable (private mode / quota) */
        }
      },

      _read() {
        try {
          const raw =
            localStorage.getItem(STORAGE_KEY) ||
            sessionStorage.getItem(STORAGE_KEY);
          return raw ? JSON.parse(raw) : null;
        } catch (_) {
          return null;
        }
      },

      _remove(area) {
        try {
          area.removeItem(STORAGE_KEY);
        } catch (_) {
          /* ignore */
        }
      },

      // Store all three spec-named fields (postal code, resolved retail location,
      // active catalog identifier). Values are treated as opaque strings — the
      // exact shape is owned by the resolution/orchestration layer (ALBPE-21).
      _normalize(input) {
        if (!input) return null;
        const catalogId =
          input.catalogId != null ? String(input.catalogId) : null;
        const retailLocation =
          input.retailLocation != null ? String(input.retailLocation) : null;
        if (!catalogId && !retailLocation) return null; // nothing meaningful to save
        return {
          postalCode: input.postalCode != null ? String(input.postalCode) : null,
          retailLocation,
          catalogId,
          version: SCHEMA_VERSION,
        };
      },

      _validate(state) {
        if (!state || state.version !== SCHEMA_VERSION) return false;
        if (!state.catalogId) return false;
        const known = this._knownCatalogIds();
        // If we have a synchronously-known set, enforce membership; otherwise
        // accept any non-empty id (the catalog layer will re-resolve if wrong).
        if (known.length && !known.includes(state.catalogId)) return false;
        return true;
      },

      // Valid market handles from theme config (no catalog JSON assets).
      _knownCatalogIds() {
        const ids = [];
        const cfg = (window.Maximize && window.Maximize.catalog) || {};
        if (Array.isArray(cfg.marketHandles)) ids.push(...cfg.marketHandles);
        if (window.Maximize && window.Maximize.defaultCatalog) {
          ids.push(window.Maximize.defaultCatalog);
        }
        return ids;
      },

      // ---- consent handling -------------------------------------------------
      _applyConsent(granted) {
        const next = granted === true;
        if (next === this._consent) return;
        this._consent = next;
        if (next) {
          this._migrate(sessionStorage, localStorage); // upgrade to persistent
        } else {
          this._migrate(localStorage, sessionStorage); // revoke -> session only
        }
        document.dispatchEvent(
          new CustomEvent(EVENT.consentChanged, {
            detail: { granted: next },
          })
        );
      },

      _migrate(fromArea, toArea) {
        try {
          const raw = fromArea.getItem(STORAGE_KEY);
          if (raw != null) {
            toArea.setItem(STORAGE_KEY, raw);
            fromArea.removeItem(STORAGE_KEY);
          }
        } catch (_) {
          /* ignore */
        }
      },

      _initConsent() {
        // Best-effort synchronous read (covers implied-consent stores).
        this._consent = readConsentSync();

        // The Customer Privacy API loads asynchronously; re-read once it's ready.
        if (window.Shopify && typeof window.Shopify.loadFeatures === "function") {
          window.Shopify.loadFeatures(
            [{ name: "consent-tracking-api", version: "0.1" }],
            () => this._applyConsent(readConsentSync())
          );
        }

        // React to the shopper accepting/declining the banner mid-session.
        document.addEventListener("visitorConsentCollected", () =>
          this._applyConsent(readConsentSync())
        );
      },

      // ---- bootstrap --------------------------------------------------------
      init() {
        this._initConsent();

        // Auto-capture the resolved catalog from the catalog layer (ALBPE-21) so
        // a chosen location is persisted without that ticket importing this file.
        document.addEventListener("maximize:catalog:ready", (e) => {
          const detail = (e && e.detail) || {};
          const market = detail.market || null;
          const catalogId = market && market.handle ? market.handle : null;
          if (!catalogId) return; // nothing resolved (e.g. no store selected yet)
          const retailLocation =
            detail.storeName || (market && market.name) || null;
          const existing = this.get() || {};
          this.save({
            postalCode: existing.postalCode || null,
            retailLocation,
            catalogId,
          });
        });

        // Restore on load. The detail lets ALBPE-21 skip re-prompting a returning
        // shopper (AC-06). ALBPE-21 must subscribe to maximize:location:restored.
        this.restore();
      },
    };

    window.Maximize.locationPersistence = store;
    store.init();
  })();
}
